import { DvmModule, MapViewerService, NavigableMinimapComponent } from '@3ddv/ngx-dvm-internal';
import { AfterViewInit, Component, computed, effect, EffectRef, ElementRef, inject, OnDestroy, Signal, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { APP_CONFIG } from '@configuration/configuration';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { Configuration } from '@models/interfaces/configuration/index.interface';
import { MenuService } from '@services/components/menu.service';
import { MinimapService } from '@services/components/minimap.service';
import { DvmService } from '@services/dvm.service';
import { FilterService } from '@services/filter.service';
import anime from 'animejs';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-minimap',
  standalone: true,
  imports: [
    DvmModule
  ],
  templateUrl: './minimap.component.html',
  styleUrl: './minimap.component.css'
})
export class MinimapComponent implements AfterViewInit, OnDestroy{

  @ViewChild('minimapContainer')
  private readonly minimapContainer!: ElementRef<HTMLElement>
  @ViewChild('navigableMinimap', { static: false })
  navigableMinimap!: NavigableMinimapComponent;
  
  // SERVICES
  public    readonly dvmService:      DvmService            = inject(DvmService);
  private   readonly filterService:   FilterService         = inject(FilterService);
  private   readonly menuService:     MenuService           = inject(MenuService);
  private   readonly minimapService:  MinimapService        = inject(MinimapService);
  private   readonly viewParam:       string | null         = inject(ActivatedRoute).snapshot.queryParamMap.get('view_id');
  private   readonly config:          Signal<Configuration> = inject(APP_CONFIG);
  
  
  // GLOBAL STATE
  protected readonly view:            Signal<AppViews|null>     = this.dvmService.view;
  protected readonly minimap:         MapViewerService          = this.dvmService.minimap;
  protected readonly enableMinimap:   Signal<boolean>           = computed((): boolean => this.config()['Dvm']['minimap'].enabled);
  protected readonly navigableMinimapEnabled:   Signal<boolean> = computed((): boolean => this.config()['Dvm']['minimap'].navigableMinimapEnabled);
  
  
  // APP VIEWS
  protected readonly appViews:        typeof AppViews       = AppViews;  
  
  // COMPONENT STATE
  private readonly subscriptions:   Subscription[]            = [];
  protected showMinimap:            boolean                   = false;
  protected useNavigable:           boolean | null            = null;
  
  get isLoaded() {
    return this.navigableMinimap.service.isLoaded();
  }

  get isMinimapHidden(): boolean {
  return (
    !this.showMinimap ||
    !this.enableMinimap() ||
    (this.useNavigable && !this.isLoaded) ||
    (!this.useNavigable &&
      (this.view() === this.appViews.Topview || !this.minimap.isLoaded()))
  );
}

  
  // EFFECTS
  private   readonly onViewChange:    EffectRef             = effect((): void => {    
    const navigableEnabled = this.navigableMinimapEnabled();
    // Si tiene un view_id, no se hace nada
    if(this.viewParam) return;

    this.useNavigable = navigableEnabled; // use navigable map if enabled

  }, { allowSignalWrites: true })
        
  ngAfterViewInit(): void {

    this.subscriptions.push(

      this.navigableMinimap.service.waitInitialize().subscribe(() => {
        this.navigableMinimap.service.getObservable('load_success').subscribe(() => {
          if(this.useNavigable) {
            this.minimapService.setVisibility(true)
          } 
        });
        this.navigableMinimap.service.getObservable('reset').subscribe(() => {
          if(!this.useNavigable) {
            this.minimapService.setVisibility(true)            
          } 
        });
      })
    )
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }
    
  private readonly onVisibilityChange: EffectRef = effect((): void => {
    const show = this.minimapService.isVisible();
    
    if(!this.enableMinimap()) return;
    
    if(show && !this.showMinimap) this.showMinimap = true;
    
    anime({
      targets:  this.minimapContainer.nativeElement,
      opacity:  show ? 1 : 0,
      duration: 300,
      easing:   'easeInOutQuad',
      complete: (): void => { 
        if(!show) {
          this.showMinimap = false }
        }
      })
  });
  
  // METHODS
  public onMinimapClick(): void {

    // Si la vista es Main, no hacemos nada
    if(this.view() === AppViews.Main || this.useNavigable) return; 

    // Forzamos el seteo null de un menu item y asi limpiamos filtros y ams si hay
    this.menuService.setMenuItem(null);

    // Reseteamos el viewer
    this.dvmService.resetViewer();

  }
  
}
